import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import orderHistoryImg from '../../assets/media/order-history.png';
import apiClient from '../../utils/api-client';
import SimpleLoader from '../Common/Loading';

const OrdersHistory = () => {
  const user = localStorage.getItem('userID');
  const [userData, setUserData] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    fetchData(); // Fetch initial data
  }, [user]);

  const fetchData = () => {
    setIsLoading(true);
    apiClient
      .get(`/order/${user}`)
      .then((res) => {
        const order = res.data?.data;

        if (Array.isArray(order)) {
          setUserData(order);
        } else {
          toast.error('Invalid response from server.');
          setUserData([]);
        }

        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load orders.');
        setIsLoading(false);
      });
  };

  const handleCancelOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true); // Open the modal via state
  };

  const onSubmit = async (data) => {
    const formData = {
      comment: data.reason,
      order_id: selectedOrderId,
    };

    try {
      await apiClient.post(`/order-cancel`, formData);
      toast.success('Order cancelled successfully!');
      setIsModalOpen(false); // Close modal after submit
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel order. Please try again later.');
    } finally {
      setSelectedOrderId(null);
    }
  };

  return (
    <div className="py-4">
      <div className="text-center mb-5">
        <h2 className="fw-bold">Order History</h2>
        <hr className="mx-auto w-25" />

        <p className="mt-3 px-md-5">
          Kindly review your order history to check purchased products, verify
          your shipping details, and track the status of your orders for a
          smooth shopping experience.
        </p>
      </div>

      <div className="table-responsive text-center">
        {userData && userData?.length === 0 ? (
          <div className="text-center my-5">
            <img
              src={orderHistoryImg}
              alt="Order History"
              className="img-fluid mx-auto d-block mb-3 rounded"
              style={{ maxWidth: 180, opacity: 0.1 }}
            />
            <p className="lead mb-4 fs-5">No orders found.</p>
            <Link
              to="/shop/"
              className="btn btn-sm btn-primary text-white"
              style={{ backgroundColor: '#800080', border: 'none' }}
            >
              Explore More Products
            </Link>
          </div>
        ) : (
          <div className="table-responsive shadow-sm rounded bg-white">
            <style>{`
              .btn-purple {
                background-color: #800080;
                border: none;
              }
              .btn-purple:hover {
                background-color: #6a006a;
              }
              .table-responsive {
                overflow-x: auto;
              }
              .table td,
              .table th {
                vertical-align: middle;
                white-space: nowrap;
              }
              .modal {
                background: rgba(0, 0, 0, 0.5);
              }
              .modal.show {
                display: block;
                z-index: 1050;
              }
              .modal-backdrop {
                z-index: -1;
              }
                .table td{
                padding: 10px;
                }
            `}</style>

            <table className="table table-striped table-hover align-middle text-center mb-0">
              <thead className="table-light">
                <tr>
                  <th>Shipping Address</th>
                  <th>Shipping Charges</th>
                  <th>Order Price</th>
                  <th>Discount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              {isLoading && (
                <tbody>
                  <tr>
                    <td colSpan="6" className="py-3">
                      <SimpleLoader />
                    </td>
                  </tr>
                </tbody>
              )}

              <tbody>
                {userData &&
                  userData.length > 0 &&
                  userData.map((orderItem, index) => (
                    <tr key={index}>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: '150px' }}
                      >
                        {orderItem.shipping_address}
                      </td>
                      <td className="fw-bold text-center">
                        Rs {orderItem.shipping_charges}
                      </td>
                      <td className="fw-bold text-success text-center">
                        Rs {orderItem.order_price}
                      </td>
                      <td className="text-danger fw-bold text-center">
                        Rs {orderItem.discounted_value || 0}
                      </td>
                      <td>
                        <span
                          className={`badge px-3 py-2 ${
                            orderItem.status.status === 'Pending'
                              ? 'bg-warning text-dark'
                              : 'bg-success'
                          }`}
                        >
                          {orderItem.status.status}
                        </span>
                      </td>
                      <td>
                        {orderItem.status.status === 'Pending' && (
                          <button
                            className="btn btn-outline-danger btn-sm me-2"
                            onClick={() =>
                              handleCancelOrder(orderItem.order_id)
                            }
                          >
                            Cancel
                          </button>
                        )}
                        <NavLink
                          to={`/order-detail/${orderItem.order_id}`}
                          className="btn btn-purple btn-sm text-white"
                        >
                          Details
                        </NavLink>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === State Controlled Modal === */}
      {isModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content border-0 shadow">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="modal-header border-0">
                  <h5 className="modal-title text-dark">Cancel Order</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold">
                    Why do you want to cancel this order?
                  </label>
                  <input
                    type="hidden"
                    name="order_id"
                    value={selectedOrderId || ''}
                    {...register('order_id')}
                  />
                  <textarea
                    placeholder="Enter your reason for cancellation"
                    className="form-control"
                    rows="3"
                    {...register('reason', { required: true })}
                  />
                </div>
                <div className="modal-footer border-0">
                  <button type="submit" className="btn btn-purple text-white">
                    Confirm Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
    </div>
  );
};

export default OrdersHistory;
